<template>
  <div class="world-home">
    <div class="world-home__decor" aria-hidden="true">
      <div class="sun" />
      <div class="island-mini">
        <span class="palm palm--a" />
        <span class="palm palm--b" />
      </div>
      <div class="sea-bar" />
    </div>

    <button
      type="button"
      class="btn-logout"
      title="退出登录"
      aria-label="退出登录"
      @click="logout"
    >
      <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
        <path
          fill="currentColor"
          d="M10 4H4v16h6v2H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h6v2zm8.5 6 4 4-4 4-1.4-1.4 1.6-1.6H10v-2h8.7l-1.6-1.6L18.5 10z"
        />
      </svg>
    </button>

    <router-link
      class="btn-profile"
      to="/profile"
      title="个人"
      aria-label="个人中心"
    >
      <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
        <path
          fill="currentColor"
          d="M12 12a4 4 0 1 0-4-4 4 4 0 0 0 4 4zm0 2c-4.42 0-8 1.79-8 4v2h16v-2c0-2.21-3.58-4-8-4z"
        />
      </svg>
    </router-link>

    <div class="layout">
      <aside class="sidebar">
        <div class="info-card">
          <p>名称： {{ displayName }}</p>
          <p>时间： {{ nowText }}</p>
        </div>

        <p v-if="listLoading" class="list-hint">班级加载中…</p>
        <div
          v-for="(item, i) in worldSlots"
          :key="item.classId + '-' + i"
          class="world-btn-wrap"
        >
          <button
            type="button"
            class="world-btn"
            :class="{ 'world-btn--active': item.active }"
            :title="'单击选中，双击进入「' + item.className + '」'"
            :disabled="openingSlot"
            @click="onWorldClick(i)"
            @dblclick.prevent="onWorldDblClick(i)"
          >
            {{ item.className }}
          </button>
          <button
            type="button"
            class="world-btn-delete"
            title="删除沙盘"
            aria-label="删除沙盘"
            :disabled="deletingSlot === getSlotKey(item, i)"
            @click.stop="onDeleteSlot(item, i)"
          >
            <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
              <path
                fill="currentColor"
                d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"
              />
            </svg>
          </button>
        </div>
      </aside>

      <div class="divider" aria-hidden="true" />

      <main class="main">
        <div class="feature-wrap">
          <div
            class="feature-card feature-card--clickable"
            role="button"
            tabindex="0"
            title="创建小岛"
            @click="goCreateIsland"
            @keyup.enter="goCreateIsland"
            @keyup.space.prevent="goCreateIsland"
          >
            <img class="feature-img" :src="featureImg" alt="">
            <div class="feature-title">创建我的小岛</div>
          </div>
        </div>
        <p class="footer-note">可上传报告次数: {{ reportQuota }}</p>
      </main>
    </div>

    <confirm-dialog
      :visible.sync="deleteConfirmOpen"
      title="删除沙盘"
      :message="deleteConfirmMessage"
      confirm-text="删除"
      loading-text="删除中…"
      variant="danger"
      :loading="!!deletingSlot"
      @confirm="confirmDelete"
      @cancel="resetDeleteConfirm"
    />

    <confirm-dialog
      :visible.sync="logoutConfirmOpen"
      title="退出登录"
      message="确定要退出当前账号吗？"
      confirm-text="退出"
      variant="danger"
      @confirm="confirmLogout"
    />
  </div>
</template>

<script>
import ConfirmDialog from '@/components/ConfirmDialog.vue'
import featureImg from '@/assets/home-feature.png'
import { deleteTimeStatisData, getSandTableData } from '@/api/psychology'
import { getUserInfo } from '@/api/user'
import { isUnauthorizedError } from '@/utils/request'
import { getUser, clearUser, saveSelectedClass } from '@/utils/auth'
import message from '@/utils/message'
import { buildUserSessionFromSandResponse } from '@/utils/sandTableRestore'
import { getClassSlotKey, getSandSession, saveSandSession } from '@/utils/sandSession'

export default {
  name: 'WorldHome',
  components: {
    ConfirmDialog
  },
  data () {
    return {
      featureImg,
      displayName: '',
      reportQuota: 0,
      worldSlots: [],
      listLoading: false,
      deletingSlot: null,
      deleteConfirmOpen: false,
      deleteConfirmMessage: '',
      deleteTarget: null,
      logoutConfirmOpen: false,
      worldClickTimer: null,
      openingSlot: false,
      nowText: ''
    }
  },
  computed: {
    activeWorld () {
      return this.worldSlots.find((item) => item.active) || null
    },
    activeWorldIndex () {
      return this.worldSlots.findIndex((item) => item.active)
    }
  },
  mounted () {
    const user = getUser()
    this.displayName = (user && user.account) || '未登录'
    this.fetchUserInfo()
    this.tick()
    this._timer = setInterval(this.tick, 1000)
  },
  beforeDestroy () {
    if (this._timer) clearInterval(this._timer)
    if (this.worldClickTimer) clearTimeout(this.worldClickTimer)
  },
  methods: {
    tick () {
      const d = new Date()
      const pad = n => (n < 10 ? '0' + n : '' + n)
      this.nowText =
        d.getFullYear() +
        '/' +
        (d.getMonth() + 1) +
        '/' +
        d.getDate() +
        ' ' +
        pad(d.getHours()) +
        ':' +
        pad(d.getMinutes()) +
        ':' +
        pad(d.getSeconds())
    },
    async fetchUserInfo () {
      const user = getUser()
      if (!user || !user.userId) {
        message.error('未获取到用户信息')
        return
      }

      this.listLoading = true
      try {
        const res = await getUserInfo(user.userId)

        if (res.status === -100) {
          clearUser()
          message.warning('登录已过期，请重新登录')
          this.$router.replace({ name: 'Login' })
          return
        }

        if (res.status !== 0) {
          message.error(res.msg || '加载班级列表失败')
          return
        }

        this.displayName = res.name || res.nickname || user.account
        this.reportQuota = res.archiveNum != null ? res.archiveNum : 0

        const classes = this.normalizeClasses(res.classesInfo || [])
        this.worldSlots = classes.map((item, i) => ({
          ...item,
          active: i === 0
        }))
        if (this.worldSlots[0]) {
          this.applyClassSelection(0)
        }
      } catch (err) {
        if (isUnauthorizedError(err)) return
        message.error(err.message || '网络异常，请稍后重试')
      } finally {
        this.listLoading = false
      }
    },
    normalizeClasses (list) {
      const seen = new Set()
      return list.filter(item => {
        const id = item.classId
        if (!id || seen.has(id)) return false
        seen.add(id)
        return true
      })
    },
    getSlotKey (item, index) {
      return getClassSlotKey(item, index)
    },
    applyClassSelection (index) {
      this.worldSlots = this.worldSlots.map((s, i) => ({
        ...s,
        active: i === index
      }))
      const selected = this.worldSlots[index]
      if (!selected) return
      const slot = getClassSlotKey(selected, index)
      saveSelectedClass({ ...selected, slot })
      const prev = getSandSession() || {}
      saveSandSession({ ...prev, slot })
    },
    onWorldClick (index) {
      if (this.openingSlot) return
      if (this.worldClickTimer) clearTimeout(this.worldClickTimer)
      this.worldClickTimer = setTimeout(() => {
        this.applyClassSelection(index)
        this.worldClickTimer = null
      }, 220)
    },
    async onWorldDblClick (index) {
      if (this.openingSlot) return
      if (this.worldClickTimer) {
        clearTimeout(this.worldClickTimer)
        this.worldClickTimer = null
      }
      this.applyClassSelection(index)
      const item = this.worldSlots[index]
      const user = getUser()
      if (!item || !user || !user.userId) {
        message.error('未获取到用户信息')
        return
      }

      const slot = getClassSlotKey(item, index)
      this.openingSlot = true
      const loadingMsgId = message.info('正在加载沙盘存档…', 0)
      try {
        const res = await getSandTableData({
          userId: String(user.userId),
          slot
        })

        if (res.status === -100) {
          clearUser()
          message.warning('登录已过期，请重新登录')
          this.$router.replace({ name: 'Login' })
          return
        }

        if (res.status !== 0) {
          message.error(res.msg || '加载沙盘失败')
          return
        }

        const userSession = buildUserSessionFromSandResponse(res, slot)
        if (!userSession.restoreSand && !userSession.name) {
          message.warning('该班级暂无沙盘存档')
          return
        }

        const prev = getSandSession() || {}
        saveSandSession({
          ...prev,
          ...userSession,
          userType: userSession.userType || prev.userType || '',
          slot,
          restoreSand: true
        })
        this.$router.push({ name: 'UserInfoUpload' })
      } catch (err) {
        if (isUnauthorizedError(err)) return
        const tip =
          err && err.code === 'TIMEOUT'
            ? '加载超时，服务端响应较慢，请稍后重试'
            : err.message || '加载失败，请稍后重试'
        message.error(tip)
      } finally {
        message.close(loadingMsgId)
        this.openingSlot = false
      }
    },
    resetDeleteConfirm () {
      if (this.deletingSlot) return
      this.deleteTarget = null
    },
    onDeleteSlot (item, index) {
      const label = item.className || '该班级'
      this.deleteTarget = { item, index }
      this.deleteConfirmMessage =
        '确定删除「' + label + '」的沙盘数据吗？删除后不可恢复。'
      this.deleteConfirmOpen = true
    },
    async confirmDelete () {
      if (!this.deleteTarget || this.deletingSlot) return

      const user = getUser()
      if (!user || !user.userId) {
        message.error('未获取到用户信息')
        this.deleteConfirmOpen = false
        return
      }

      const { item, index } = this.deleteTarget
      const slotKey = this.getSlotKey(item, index)
      this.deletingSlot = slotKey
      try {
        const res = await deleteTimeStatisData({
          userId: user.userId,
          slot: slotKey,
          time: item.time || ''
        })

        if (res.status === -100) {
          clearUser()
          message.warning('登录已过期，请重新登录')
          this.deleteConfirmOpen = false
          this.$router.replace({ name: 'Login' })
          return
        }

        if (res.status !== 0) {
          message.error(res.msg || '删除失败')
          return
        }

        message.success('沙盘已删除')
        this.deleteConfirmOpen = false
        this.deleteTarget = null
        await this.fetchUserInfo()
      } catch (err) {
        if (isUnauthorizedError(err)) return
        message.error(err.message || '删除失败，请稍后重试')
      } finally {
        this.deletingSlot = null
      }
    },
    logout () {
      this.logoutConfirmOpen = true
    },
    confirmLogout () {
      clearUser()
      this.logoutConfirmOpen = false
      this.$router.push({ name: 'Login' })
    },
    persistActiveSlot () {
      const idx = this.activeWorldIndex
      const selected = this.activeWorld
      if (!selected || idx < 0) return
      const slot = getClassSlotKey(selected, idx)
      saveSelectedClass({ ...selected, slot })
      const prev = getSandSession() || {}
      saveSandSession({ ...prev, slot })
    },
    goCreateIsland () {
      this.persistActiveSlot()
      const idx = this.activeWorldIndex
      const selected = this.activeWorld
      const slot =
        selected && idx >= 0 ? getClassSlotKey(selected, idx) : '1'
      saveSandSession({
        slot,
        restoreSand: false
      })
      this.$router.push({ name: 'UserInfoUpload' })
    }
  }
}
</script>

<style scoped>
.world-home {
  position: relative;
  min-height: 100vh;
  box-sizing: border-box;
  padding: 20px 20px 56px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'PingFang SC',
    'Hiragino Sans GB', 'Microsoft YaHei', sans-serif;
  background: linear-gradient(180deg, #a9dafb 0%, #8ec8f5 42%, #7ab8e8 100%);
  color: #1a2b3c;
  overflow: hidden;
}

.world-home__decor {
  pointer-events: none;
  position: absolute;
  inset: 0;
}

.sun {
  position: absolute;
  top: 12px;
  left: 16px;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: radial-gradient(circle at 35% 35%, #fff9c4, #ffeb3b 45%, #ffc107 100%);
  box-shadow:
    0 0 0 6px rgba(255, 235, 59, 0.35),
    0 0 40px rgba(255, 193, 7, 0.45);
}

.island-mini {
  position: absolute;
  right: 6%;
  bottom: 48px;
  width: 72px;
  height: 40px;
  background: #f5e6c8;
  border-radius: 50% 50% 45% 45%;
  box-shadow: inset 0 -4px 0 rgba(180, 160, 120, 0.35);
}

.palm {
  position: absolute;
  bottom: 18px;
  width: 4px;
  height: 22px;
  background: #5d8c3e;
  border-radius: 2px;
}

.palm::after {
  content: '';
  position: absolute;
  left: 50%;
  bottom: 14px;
  width: 28px;
  height: 18px;
  margin-left: -14px;
  background: radial-gradient(ellipse at 50% 100%, #6ba644 0%, #4a7c34 70%);
  border-radius: 50% 50% 40% 40%;
}

.palm--a {
  left: 18px;
}

.palm--b {
  right: 16px;
}

.sea-bar {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 28px;
  background: linear-gradient(180deg, #1e5799 0%, #0d3d6b 100%);
}

.btn-logout {
  position: fixed;
  top: 16px;
  right: 16px;
  z-index: 20;
  width: 44px;
  height: 44px;
  border: none;
  border-radius: 8px;
  background: #e53935;
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(229, 57, 53, 0.45);
}

.btn-logout:hover {
  filter: brightness(1.05);
}

.btn-profile {
  position: fixed;
  bottom: 20px;
  left: 20px;
  z-index: 20;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: linear-gradient(180deg, #9e9e9e, #757575);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  text-decoration: none;
}

.btn-profile:hover {
  filter: brightness(1.08);
}

.layout {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: stretch;
  max-width: 1100px;
  margin: 56px auto 0;
  min-height: calc(100vh - 120px);
}

.sidebar {
  width: 260px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.info-card {
  background: #fff;
  border-radius: 12px;
  padding: 14px 16px;
  box-shadow: 0 2px 10px rgba(30, 90, 140, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.9);
}

.info-card p {
  margin: 0 0 6px;
  font-size: 14px;
  color: #263238;
  line-height: 1.45;
}

.info-card p:last-child {
  margin-bottom: 0;
}

.world-btn-wrap {
  position: relative;
}

.world-btn {
  display: block;
  width: 100%;
  padding: 14px 40px 14px 16px;
  border-radius: 12px;
  border: 2px solid transparent;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  text-align: center;
  background: #fff;
  color: #1a2b3c;
  box-shadow: 0 2px 8px rgba(30, 90, 140, 0.1);
  transition: border-color 0.15s, box-shadow 0.15s;
}

.world-btn-delete {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: none;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.92);
  color: #c62828;
  cursor: pointer;
  box-shadow: 0 2px 6px rgba(30, 90, 140, 0.18);
  transition: background 0.15s, color 0.15s, transform 0.12s;
}

.world-btn-delete:hover:not(:disabled) {
  background: #ffebee;
  color: #b71c1c;
  transform: scale(1.05);
}

.world-btn-delete:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.world-btn-wrap .world-btn--active + .world-btn-delete {
  background: rgba(255, 255, 255, 0.95);
}

.world-btn:disabled {
  opacity: 0.65;
  cursor: wait;
}

.world-btn:hover {
  box-shadow: 0 4px 14px rgba(30, 90, 140, 0.16);
}

.world-btn--active {
  background: #ffeb3b;
  border-color: #ff9800;
  color: #5d4037;
  box-shadow: 0 4px 14px rgba(255, 152, 0, 0.35);
}

.list-hint {
  margin: 0;
  padding: 10px 12px;
  font-size: 13px;
  color: #546e7a;
  background: rgba(255, 255, 255, 0.85);
  border-radius: 10px;
  text-align: center;
}

.divider {
  width: 14px;
  flex-shrink: 0;
  margin: 0 10px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.85);
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.5);
}

.main {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: 8px;
}

.feature-wrap {
  width: 100%;
  max-width: 560px;
}

.feature-card {
  position: relative;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 12px 40px rgba(20, 60, 100, 0.25);
  border: 3px solid rgba(255, 255, 255, 0.85);
  background: #0d47a1;
}

.feature-card--clickable {
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.feature-card--clickable:hover {
  transform: translateY(-2px);
  box-shadow: 0 16px 48px rgba(20, 60, 100, 0.32);
}

.feature-card--clickable:focus {
  outline: none;
  box-shadow: 0 0 0 3px rgba(66, 165, 245, 0.55), 0 12px 40px rgba(20, 60, 100, 0.25);
}

.feature-img {
  display: block;
  width: 100%;
  height: auto;
  vertical-align: middle;
}

.feature-title {
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  padding: 16px 20px;
  font-size: 22px;
  font-weight: 800;
  letter-spacing: 0.06em;
  color: #fff;
  text-shadow:
    0 1px 2px rgba(0, 0, 0, 0.45),
    0 4px 24px rgba(0, 40, 80, 0.5);
  pointer-events: none;
}

.footer-note {
  margin-top: 16px;
  font-size: 14px;
  color: #263238;
  font-weight: 500;
  text-align: center;
}

@media (max-width: 768px) {
  .world-home {
    padding: 14px 14px 56px;
  }
  .sun {
    top: 10px;
    left: 12px;
    width: 40px;
    height: 40px;
    box-shadow:
      0 0 0 4px rgba(255, 235, 59, 0.35),
      0 0 28px rgba(255, 193, 7, 0.45);
  }
  .island-mini {
    right: 4%;
    bottom: 44px;
    width: 52px;
    height: 28px;
  }
  .palm {
    bottom: 12px;
    height: 16px;
    width: 3px;
  }
  .palm::after {
    bottom: 10px;
    width: 20px;
    height: 14px;
    margin-left: -10px;
  }
  .palm--a {
    left: 12px;
  }
  .palm--b {
    right: 12px;
  }
  .sea-bar {
    height: 22px;
  }
  .btn-logout {
    top: 12px;
    right: 12px;
    width: 38px;
    height: 38px;
  }
  .btn-logout svg {
    width: 18px;
    height: 18px;
  }
  .btn-profile {
    bottom: 14px;
    left: 14px;
    width: 42px;
    height: 42px;
  }
  .btn-profile svg {
    width: 18px;
    height: 18px;
  }
  .layout {
    flex-direction: column;
    max-width: 100%;
    margin: 50px auto 0;
    min-height: auto;
    gap: 16px;
  }
  .sidebar {
    width: 100%;
    gap: 10px;
  }
  .info-card {
    padding: 12px 14px;
    border-radius: 10px;
  }
  .info-card p {
    font-size: 13px;
  }
  .world-btn {
    padding: 12px 38px 12px 14px;
    font-size: 15px;
  }
  .world-btn-delete {
    width: 26px;
    height: 26px;
    top: 6px;
    right: 6px;
  }
  .divider {
    display: none;
  }
  .main {
    padding-top: 0;
    width: 100%;
  }
  .feature-wrap {
    max-width: 100%;
  }
  .feature-card {
    border-radius: 14px;
    border-width: 2px;
  }
  .feature-title {
    font-size: 18px;
    padding: 14px 16px;
  }
  .footer-note {
    margin-top: 12px;
    font-size: 13px;
  }
}
</style>



// WEBPACK FOOTER //
// src/views/WorldHome.vue